import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { Modal, Form, Select, Input, message, Radio, Space, Typography, Image } from 'antd';
import { orderApi } from '../../../services/process/order';
import { OrderEntity } from '../../../types/process/order';
import ResourceUploader from '../../../components/Business/ResourceUploader';

const { TextArea } = Input;
const { Option } = Select;

export type CancelOrderTemplateKey = 'cancelOrder' | 'shipIntercept' | 'logisticsIntercept';

export interface CancelOrderModalRef {
  open: (config: { order: OrderEntity; template?: CancelOrderTemplateKey; mode?: 'edit' | 'view'; confirmText?: string; onConfirm?: () => Promise<void> | void }) => void;
  close: () => void;
}

interface CancelOrderModalProps {
  onSuccess?: () => void;
}

const CANCEL_CATEGORIES = [
  { label: '用户原因', value: '用户原因' },
  { label: '联系不上用户', value: '联系不上用户' },
  { label: '用户资质问题', value: '用户资质问题' },
  { label: '租金/押金/收益问题', value: '租金/押金/收益问题' },
  { label: '地区原因', value: '地区原因' },
];

const CANCEL_REASON_MAP: Record<string, Array<{ label: string; value: string }>> = {
  用户原因: [
    { label: '用户重复下单', value: '用户重复下单' },
    { label: '用户计划有变', value: '用户计划有变' },
    { label: '已于用户协商一致', value: '已于用户协商一致' },
    { label: '订单信息填写/选择有误', value: '订单信息填写/选择有误' },
    { label: '用户资金问题', value: '用户资金问题' },
    { label: '30mins内用户，订单自动关闭', value: '30mins内用户，订单自动关闭' },
    { label: '进单前用户申请取消', value: '进单前用户申请取消' },
    { label: '不接受租赁服务设备', value: '不接受租赁服务设备' },
    { label: '不接受有锁', value: '不接受有锁' },
    { label: '其他', value: '其他' },
  ],
  联系不上用户: [
    { label: '无人接听/一直通话中/接听后挂断', value: '无人接听/一直通话中/接听后挂断' },
    { label: '空号/关机', value: '空号/关机' },
    { label: '其他', value: '其他' },
  ],
  用户资质问题: [
    { label: '芝麻分低', value: '芝麻分低' },
    { label: '有逾期记录（如花呗逾期等）', value: '有逾期记录（如花呗逾期等）' },
    { label: '用户年龄未满22岁/年龄大于45岁', value: '用户年龄未满22岁/年龄大于45岁' },
    { label: '无法提供本人下单信息', value: '无法提供本人下单信息' },
    { label: '不配合审核', value: '不配合审核' },
    { label: '疑似套现/刷流水/代他人下单', value: '疑似套现/刷流水/代他人下单' },
    { label: '风控审核不通过', value: '风控审核不通过' },
    { label: '其他', value: '其他' },
  ],
  '租金/押金/收益问题': [
    { label: '租金过高', value: '租金过高' },
    { label: '押金过高', value: '押金过高' },
    { label: '利润较低', value: '利润较低' },
    { label: '不接受预收多期租金', value: '不接受预收多期租金' },
    { label: '其他', value: '其他' },
  ],
  地区原因: [
    { label: '地区限制，不支持发货', value: '地区限制，不支持发货' },
    { label: '地区偏远，不支持发货', value: '地区偏远，不支持发货' },
    { label: '该地区为逾期订单高发区', value: '该地区为逾期订单高发区' },
    { label: '其他', value: '其他' },
  ],
};

const CancelOrderModal = forwardRef<CancelOrderModalRef, CancelOrderModalProps>(({ onSuccess }, ref) => {
  const [form] = Form.useForm();
  const [open, setOpen] = useState(false);
  const [order, setOrder] = useState<OrderEntity | null>(null);
  const [templateKey, setTemplateKey] = useState<CancelOrderTemplateKey>('cancelOrder');
  const [mode, setMode] = useState<'edit' | 'view'>('edit');
  const [viewConfirm, setViewConfirm] = useState<{ text?: string; onConfirm?: () => Promise<void> | void } | null>(null);
  const [confirming, setConfirming] = useState(false);

  const templates = useMemo(() => ({
    cancelOrder: { title: '取消订单' as const, defaultCancelType: 'CANCEL_ORDER' },
    shipIntercept: { title: '发货拦截' as const, defaultCancelType: 'SHIP_INTERCEPT' },
    logisticsIntercept: { title: '物流拦截' as const, defaultCancelType: 'LOGISTICS_INTERCEPT' },
  }), []);

  const template = templates[templateKey];
  const isView = mode === 'view';
  const cancelCategory = Form.useWatch('cancelCategory', form) as string | undefined;

  // Determine effective cancel type: from template (edit) or order (view)
  const displayCancelType = isView ? order?.cancelType : template.defaultCancelType;
  
  // Resolve title based on cancelType for view mode
  const modalTitle = useMemo(() => {
    if (!isView) {
        if (templateKey === 'shipIntercept' || templateKey === 'logisticsIntercept') return '请选择拦截原因';
        return '请选择取消原因';
    }
    if (displayCancelType === 'SHIP_INTERCEPT') return '发货拦截详情';
    if (displayCancelType === 'LOGISTICS_INTERCEPT') return '物流拦截详情';
    return '取消订单详情';
  }, [isView, templateKey, displayCancelType]);

  useEffect(() => {
    if (open && order) {
      form.resetFields();
      if (isView) {
        // Populate form for view mode
        form.setFieldsValue({
          cancelType: order.cancelType,
          cancelCategory: order.cancelCategory,
          cancelReason: order.cancelReason,
          cancelEvidence: order.cancelEvidence,
        });
      } else {
        // Init for edit mode
        if (template.defaultCancelType) {
          form.setFieldsValue({ cancelType: template.defaultCancelType });
        }
      }
    }
  }, [open, order, isView, form, template.defaultCancelType]);

  useImperativeHandle(ref, () => ({
    open: (config) => {
      setOrder(config.order);
      setTemplateKey(config.template || 'cancelOrder');
      setMode(config.mode || 'edit');
      setViewConfirm(config.onConfirm ? { text: config.confirmText, onConfirm: config.onConfirm } : null);
      setOpen(true);
    },
    close: () => {
      setOpen(false);
      setOrder(null);
      setTemplateKey('cancelOrder');
      setMode('edit');
      setViewConfirm(null);
      setConfirming(false);
    },
  }), []);

  const handleSubmit = async () => {
    if (isView) {
      if (!viewConfirm?.onConfirm) {
        setOpen(false);
        return;
      }
      setConfirming(true);
      try {
        await viewConfirm.onConfirm();
        setOpen(false);
      } finally {
        setConfirming(false);
      }
      return;
    }
    try {
      const values = await form.validateFields();
      if (order) {
        await orderApi.cancelOrder(order.orderNo, values);
        message.success('操作成功');
        onSuccess?.();
        setOpen(false);
      }
    } catch (e) {
      // ignore
    }
  };

  const renderEvidence = () => {
    if (!isView) {
      return <ResourceUploader 
        dir="cancel_order/evidence" 
        maxCount={9}                                 
        initialRender='dropzone' 
        />;
    }
    // View mode evidence display
    const ev = order?.cancelEvidence;
    let urls: string[] = [];
    if (Array.isArray(ev)) {
      urls = ev.filter((v): v is string => typeof v === 'string' && v.length > 0);
    } else if (typeof ev === 'string' && ev.length > 0) {
      urls = [ev];
    } else if (typeof ev === 'object' && ev?.evidenceUrls && Array.isArray(ev.evidenceUrls)) {
      urls = ev.evidenceUrls;
    }

    if (urls.length === 0) return <span style={{ color: '#999' }}>无</span>;

    return (
      <Image.PreviewGroup>
        <Space wrap>
          {urls.map((u, i) => (
            <Image key={i} src={u} width={80} height={80} style={{ objectFit: 'cover', borderRadius: 4 }} />
          ))}
        </Space>
      </Image.PreviewGroup>
    );
  };

  const reasonOptions = useMemo(() => {
    if (isView) {
      const v = order?.cancelReason;
      return v ? [{ label: v, value: v }] : [];
    }
    if (!cancelCategory) return [];
    return CANCEL_REASON_MAP[cancelCategory] || [];
  }, [cancelCategory, isView, order?.cancelReason]);

  return (
    <Modal
      title={modalTitle}
      open={open}
      onOk={handleSubmit}
      onCancel={() => setOpen(false)}
      footer={isView && !viewConfirm?.onConfirm ? null : undefined}
      okText={isView ? (viewConfirm?.text || '确认') : undefined}
      okButtonProps={isView && viewConfirm?.onConfirm ? { danger: true } : undefined}
      confirmLoading={isView && viewConfirm?.onConfirm ? confirming : undefined}
      width={600}
    >
      <Form
        form={form}
        layout="vertical"
        disabled={isView}
        onValuesChange={(changedValues) => {
          if (!isView && Object.prototype.hasOwnProperty.call(changedValues, 'cancelCategory')) {
            form.setFieldsValue({ cancelReason: undefined });
          }
        }}
      >
        {/* Hidden cancelType field for submission */}
        <Form.Item name="cancelType" hidden>
          <Input />
        </Form.Item>

        <Form.Item name="cancelCategory" label="取消类型" rules={[{ required: !isView, message: '请选择取消类型' }]}>
          <Radio.Group>
            <Space direction="vertical">
              <Space wrap>
                 <Typography.Text type="secondary" style={{ marginRight: 8 }}>[用户]</Typography.Text>
                 {CANCEL_CATEGORIES.slice(0, 3).map(c => (
                   <Radio key={c.value} value={c.value}>{c.label}</Radio>
                 ))}
              </Space>
              <Space wrap>
                 <Typography.Text type="secondary" style={{ marginRight: 8 }}>[其他]</Typography.Text>
                 {CANCEL_CATEGORIES.slice(3).map(c => (
                   <Radio key={c.value} value={c.value}>{c.label}</Radio>
                 ))}
              </Space>
            </Space>
          </Radio.Group>
        </Form.Item>

        <Form.Item name="cancelReason" label="取消原因" rules={[{ required: !isView, message: '请选择取消原因' }]}>
          <Select placeholder="请选择取消原因" allowClear disabled={!isView && !cancelCategory}>
            {reasonOptions.map((r) => (
              <Option key={r.value} value={r.value}>
                {r.label}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item label="证据截图" name={isView ? undefined : "cancelEvidence"}>
          {renderEvidence()}
        </Form.Item>
      </Form>
    </Modal>
  );
});

export default CancelOrderModal;
